import React, { useEffect, useState } from 'react';

interface ListInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
  options: string[];
  required?: boolean;
  forceType?: "select" | "list";
}



const ListInput: React.FC<ListInputProps> = ({ label, name, value, onChange, options, required = false, forceType }) => {
  // Hydration-safe browser detection
  const [isDesktopWeb, setIsDesktopWeb] = useState<boolean | null>(null);
  useEffect(() => {
    // Only run on client
    const isDesktop = !/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent);
    setIsDesktopWeb(isDesktop);
  }, []);

  // SSR: always render select for consistency
  let showSelect = forceType === "select" || (!forceType && options.length < 4);
  let showList = forceType === "list" || (!forceType && options.length >= 4);

  // After hydration, use desktop/mobile logic
  if (isDesktopWeb !== null) {
    showSelect = isDesktopWeb || forceType === "select" || (!forceType && options.length < 4);
    showList = !isDesktopWeb && (forceType === "list" || (!forceType && options.length >= 4));
  } else {
    // During SSR/hydration, always render select for both
    showSelect = true;
    showList = false;
  }

  const inputId = `list-input-${name}`

  return (
    <div className="mb-4">
      <label htmlFor={inputId} className="block text-primary-dark font-medium mb-2">{label}</label>
      {showSelect && (
        <select
          id={inputId}
          name={name}
          value={value}
          onChange={onChange as (e: React.ChangeEvent<HTMLSelectElement>) => void}
          required={required}
          className="w-full bg-primary-light/10 border-none rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-primary-light transition text-primary-dark shadow-md hover:bg-primary-light/20"
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )}
      {showList && (
        <div
          className={
            options.length === 4
              ? "grid grid-cols-2 gap-3"
              : "flex flex-wrap gap-3"
          }
        >
          {options.map(opt => {
            const isSelected = value === opt;
            return (
              <label
                key={opt}
                className={`cursor-pointer px-5 py-3 rounded-lg border-2 transition select-none text-center min-w-[80px] focus:outline-none focus-visible:outline-none active:outline-none ${
                  isSelected
                    ? 'bg-primary-light text-white border-primary-light shadow-md font-bold'
                    : 'bg-primary-light/10 text-primary-dark border-primary-light/30 hover:bg-primary-light/20'
                }`}
                style={{ display: 'inline-block' }}
                tabIndex={0}
              >
                <input
                  type="radio"
                  name={name}
                  value={opt}
                  checked={isSelected}
                  onChange={onChange as (e: React.ChangeEvent<HTMLInputElement>) => void}
                  required={required}
                  className="hidden focus:outline-none focus-visible:outline-none active:outline-none"
                  tabIndex={-1}
                />
                {opt}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ListInput;
